import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  BookUser, Search, Loader2, Plus, Pencil, Trash2, Phone, Mail,
  MessageCircle, Contact as ContactIcon,
} from 'lucide-react';
import { WhatsAppComposeDialog } from '../components/WhatsAppComposeDialog';

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);       // contact being edited (or {} for new)
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [waTarget, setWaTarget] = useState(null);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/contacts');
      setContacts(data.contacts || []);
    } catch {
      toast.error('Could not load contacts');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const openNew = () => { setForm({ name: '', phone: '', email: '' }); setEditing({}); };
  const openEdit = (c) => { setForm({ name: c.name || '', phone: c.phone || '', email: c.email || '' }); setEditing(c); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Enter a name'); return; }
    if (!form.phone.trim()) { toast.error('Enter a phone number'); return; }
    setSaving(true);
    try {
      if (editing && editing.id) {
        await api.put(`/contacts/${editing.id}`, form);
        toast.success('Contact updated');
      } else {
        await api.post('/contacts', form);
        toast.success('Contact added');
      }
      setEditing(null);
      fetchContacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save contact');
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/contacts/${deleteTarget.id}`);
      toast.success('Contact deleted');
      setDeleteTarget(null);
      fetchContacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete contact');
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = contacts.filter((c) =>
    !q || (c.name || '').toLowerCase().includes(q) || (c.phone || '').includes(q) || (c.email || '').toLowerCase().includes(q)
  );

  return (
    <Layout>
      <div data-testid="contacts-page" className="p-4 sm:p-6 max-w-5xl mx-auto space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center">
              <BookUser className="w-6 h-6 text-emerald-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Contacts</h1>
              <p className="text-sm text-slate-500">Address book synced to hello@helioses.co.uk — not customers</p>
            </div>
          </div>
          <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="contacts-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add contact
          </Button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="contacts-search"
            placeholder="Search by name, number or email…"
            className="pl-9"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-14 text-center">
            <ContactIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">{contacts.length === 0 ? 'No contacts yet. Add one, or save a WhatsApp number as a contact.' : 'No contacts match your search.'}</p>
          </CardContent></Card>
        ) : (
          <div className="grid gap-2.5" data-testid="contacts-list">
            {filtered.map((c) => (
              <Card key={c.id} data-testid={`contact-row-${c.id}`} className="hover:shadow-sm transition-shadow">
                <CardContent className="py-3 px-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <ContactIcon className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-slate-800 truncate">{c.name}</span>
                      {c.source === 'whatsapp_app' && (
                        <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-200 bg-emerald-50">WhatsApp</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                      {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                      {c.email && <span className="inline-flex items-center gap-1 truncate"><Mail className="w-3 h-3" /> {c.email}</span>}
                    </div>
                  </div>
                  {c.phone && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600" title="Message on WhatsApp"
                      onClick={() => setWaTarget(c)} data-testid={`contact-whatsapp-${c.id}`}>
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit"
                    onClick={() => openEdit(c)} data-testid={`contact-edit-${c.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" title="Delete"
                    onClick={() => setDeleteTarget(c)} data-testid={`contact-delete-${c.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-sm" data-testid="contact-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing && editing.id ? 'Edit contact' : 'Add contact'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600">Name</label>
              <Input data-testid="contact-name-input" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Dave the Roofer" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600">Phone</label>
              <Input data-testid="contact-phone-input" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="e.g. 07700 900123" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600">Email (optional)</label>
              <Input data-testid="contact-email-input" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@example.com" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="contact-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="contact-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this contact?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.name} will be removed from the address book (and the shared MS365 contacts).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="contact-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <WhatsAppComposeDialog
        open={!!waTarget}
        onOpenChange={(o) => !o && setWaTarget(null)}
        phone={waTarget?.phone || ''}
      />
    </Layout>
  );
}
